import { useFocusRing } from '@react-native-aria/focus'
import clsx from 'clsx'
import { useCallback, useImperativeHandle, useRef, useState } from 'react'
import type { NativeSyntheticEvent, TextInput } from 'react-native'

import { mapPropsVariants } from '@/src/general/utils/mapPropsVariants'

import type { TInputProps } from './Input.type'
import { inputVariants } from './variants/inputVariants'

export function useInput(originalProps: TInputProps) {
  const [props, variantProps] = mapPropsVariants(originalProps, inputVariants.variantKeys)

  const {
    ref,
    label,
    type,
    autoCapitalize,
    autoCorrect,
    maxLength,
    placeholder,
    description,
    errorMessage,
    className,
    classNames,
    startIcon,
    endIcon,
    autofocus,
    isDisabled,
    secureTextEntry,
    value = '',
    onBlur,
    onChange,
    onChangeText,
    onFocus,
    onLayout,
    onContentSizeChange,
    onKeyPress,
    onSubmitEditing,
    testID,
  } = props

  const inputRef = useRef<TextInput>(null)
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- ref bridge
  const [isFocused, setIsFocused] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  useImperativeHandle(ref, () => inputRef.current as TextInput)

  const { isFocusVisible, focusProps } = useFocusRing()
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- useFocusRing focusProps typing
  const { onBlur: focusRingOnBlur, onFocus: focusRingOnFocus } = focusProps as {
    onBlur: (e: NativeSyntheticEvent<unknown>) => void
    onFocus: (e: NativeSyntheticEvent<unknown>) => void
  }

  const [isInitialFocus, setIsInitialFocus] = useState(autofocus)

  const baseStyles = clsx(classNames?.base, className)

  const hasPlaceholder = !!props.placeholder
  const hasHelper = !!description || !!errorMessage

  const hasStartIcon = !!startIcon

  const slots = inputVariants({
    ...variantProps,
  })

  const getBaseProps = useCallback(() => {
    return {
      dataSet: { slot: 'base' },
      className: slots.base({ class: baseStyles }),
    }
  }, [slots, baseStyles])

  const getLabelProps = useCallback(() => {
    return {
      dataSet: { slot: 'label' },
      className: slots.label({ class: classNames?.label, isError: !!errorMessage }),
    }
  }, [slots, classNames?.label, errorMessage])

  const inputSlot = slots.input

  const getInputProps = () => {
    return {
      ref: inputRef,
      secureTextEntry,
      autoCorrect,
      autoCapitalize,
      dataSet: { slot: 'input' },
      className: inputSlot({
        class: clsx(classNames?.input),
      }),
      textContentType: type,
      placeholder,
      maxLength,
      readOnly: isDisabled,
      onFocus: (event: NativeSyntheticEvent<{ target: number }>) => {
        setIsFocused(true)
        focusRingOnFocus?.(event)
        onFocus?.(event)
      },
      onBlur: (event: NativeSyntheticEvent<{ target: number }>) => {
        setIsInitialFocus(false)
        setIsFocused(false)
        focusRingOnBlur?.(event)
        onBlur?.(event)
      },
      onKeyPress,
      onChange,
      onChangeText,
      value,
      onLayout,
      onContentSizeChange,
      onSubmitEditing,
      testID,
    }
  }

  const getInputWrapperProps = () => {
    return {
      dataSet: { slot: 'input-wrapper', 'focus-visible': isFocusVisible && !isInitialFocus },
      className: slots.inputWrapper({
        class: clsx(classNames?.inputWrapper),
      }),
    }
  }

  const getIconLeftProps = () => {
    return {
      dataSet: { slot: 'input-icon-left' },
      className: clsx(
        slots.iconLeft({
          class: classNames?.iconLeft,
        }),
        slots.icon({
          class: classNames?.icon,
        }),
      ),
    }
  }

  const getIconRightProps = () => {
    return {
      dataSet: { slot: 'input-icon-right' },
      className: clsx(
        slots.iconRight({
          class: classNames?.iconRight,
        }),
        slots.icon({
          class: classNames?.icon,
        }),
      ),
    }
  }

  const getMainWrapperProps = () => {
    return {
      className: slots.mainWrapper({
        class: clsx(classNames?.mainWrapper),
      }),
      dataSet: { hover: isHovered, focus: isFocused, slot: 'main-wrapper' },
      tabIndex: -1,
      onPointerEnter: () => setIsHovered(true),
      onPointerLeave: () => setIsHovered(false),
      onPress: () => {
        inputRef?.current?.focus()
      },
    } as const
  }

  const getHelperWrapperProps = () => {
    return {
      dataSet: { slot: 'helper-wrapper' },
      className: slots.helperWrapper({
        class: clsx(classNames?.helperWrapper),
      }),
    }
  }

  const getDescriptionProps = () => {
    return {
      dataSet: { slot: 'description' },
      className: slots.description({ class: clsx(classNames?.description) }),
    }
  }

  const getErrorMessageProps = () => {
    return {
      'data-slot': 'error-message',
      className: slots.errorMessage({ class: clsx(classNames?.errorMessage) }),
    }
  }

  return {
    classNames,
    ref,
    label,
    description,
    startIcon,
    endIcon,
    hasHelper,
    hasStartIcon,
    hasPlaceholder,
    errorMessage,
    isFocused,
    setIsFocused,
    getBaseProps,
    getLabelProps,
    getInputProps,
    getMainWrapperProps,
    getInputWrapperProps,
    getHelperWrapperProps,
    getDescriptionProps,
    getErrorMessageProps,
    getIconLeftProps,
    getIconRightProps,
  }
}
